import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const features = [
  { icon: 'ri-ruler-line', title: 'Perfect Fit', desc: 'Measured to your exact proportions — collar, chest, sleeve, and body length.' },
  { icon: 'ri-palette-line', title: 'Endless Fabrics', desc: 'Choose from Egyptian cotton, linen, Oxford cloth, and more in hundreds of colors.' },
  { icon: 'ri-settings-3-line', title: 'Custom Details', desc: 'Personalize collar style, cuff type, placket, monogram, and button choice.' },
  { icon: 'ri-shield-check-line', title: 'Premium Construction', desc: 'Single-needle stitching and hand-finished details for lasting quality.' },
  { icon: 'ri-home-line', title: 'House Calls', desc: 'We make house and office calls for a seamless, convenient fitting experience.' },
  { icon: 'ri-refresh-line', title: 'Reorder Anytime', desc: 'Once your pattern is on file, reordering your favorite shirt is effortless.' },
];

const styles = [
  { label: 'Dress Shirts', img: 'https://public.readdy.ai/ai/img_res/cdef3dff91163ad2fc3e8ad268dfbb1e.jpg' },
  { label: 'Casual Shirts', img: 'https://readdy.ai/api/search-image?query=custom%20casual%20linen%20shirt%20bespoke%20tailored%20light%20blue%20relaxed%20fit%20premium%20menswear%20studio%20photography%20modern%20style&width=400&height=500&seq=shirt2&orientation=portrait' },
  { label: 'Formal Shirts', img: 'https://readdy.ai/api/search-image?query=custom%20formal%20tuxedo%20shirt%20bespoke%20white%20pleated%20front%20french%20cuffs%20black%20tie%20premium%20menswear%20studio%20photography&width=400&height=500&seq=shirt3&orientation=portrait' },
];

const faqs = [
  { q: 'How long does a custom shirt take?', a: 'Custom shirts typically take 3–4 weeks from your initial fitting to delivery.' },
  { q: 'What fabrics are available?', a: 'We carry Egyptian cotton, Sea Island cotton, linen, and blended fabrics in hundreds of patterns and colors.' },
  { q: 'Can I add a monogram?', a: 'Absolutely. We offer monogramming on the cuff, chest pocket, or collar in a variety of thread colors and fonts.' },
  { q: 'What is the minimum order?', a: 'There is no minimum order. You can order a single shirt or a full wardrobe — we accommodate all needs.' },
];

const CustomShirtsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative h-[70vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=luxury%20custom%20shirt%20tailor%20bespoke%20menswear%20premium%20fabric%20selection%20wellesley%20massachusetts%20elegant%20showroom%20warm%20lighting%20professional&width=1400&height=700&seq=shirthero&orientation=landscape"
          alt="Custom Shirts"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/85 to-gray-900/40"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#C4A962] font-semibold tracking-widest uppercase text-sm mb-4">Custom Clothiers — Wellesley, MA</p>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Custom Shirts<br />Crafted for You
          </h1>
          <p className="text-xl text-gray-200 max-w-xl mb-8">
            Made-to-measure shirts in premium fabrics with every detail personalized to your taste and lifestyle.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/contact" className="bg-[#C4A962] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#B39952] transition-colors whitespace-nowrap">
              Book a Free Consultation
            </Link>
            <a href="tel:781-237-3166" className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors whitespace-nowrap">
              <i className="ri-phone-line mr-2"></i>781-237-3166
            </a>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">The Custom Shirt Difference</h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">Every shirt is built around your body, your style, and your wardrobe needs.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((f) => (
              <div key={f.title} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center bg-[#C4A962]/10 rounded-xl mb-5">
                  <i className={`${f.icon} text-[#C4A962] text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Styles */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Shirts for Every Occasion</h2>
            <p className="text-lg text-gray-500">From the boardroom to the weekend — we craft shirts for every moment.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {styles.map((s) => (
              <div key={s.label} className="relative rounded-2xl overflow-hidden group cursor-pointer">
                <img src={s.img} alt={s.label} className="w-full h-80 object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <span className="text-white text-2xl font-bold">{s.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold mb-4">How It Works</h2>
            <p className="text-gray-400 text-lg">A simple, enjoyable process from first meeting to final delivery.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', desc: 'Meet your clothier and discuss your style, fabric preferences, and wardrobe goals.' },
              { step: '02', title: 'Fabric & Details', desc: 'Select your fabric, collar, cuffs, buttons, and any personalization options.' },
              { step: '03', title: 'Measurements', desc: 'We take precise measurements to create your unique pattern on file.' },
              { step: '04', title: 'Delivery', desc: 'Your finished shirts are delivered and adjusted until the fit is perfect.' },
            ].map((s) => (
              <div key={s.step} className="text-center">
                <div className="text-5xl font-bold text-[#C4A962]/30 mb-3">{s.step}</div>
                <h3 className="text-xl font-bold mb-2">{s.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.q} className="border border-gray-200 rounded-xl p-6">
                <h3 className="font-bold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#C4A962]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Start Your Custom Shirt Order</h2>
          <p className="text-white/80 text-lg mb-8">Visit our Wellesley showroom or request a house call — consultations are always complimentary.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="bg-white text-[#C4A962] px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors whitespace-nowrap">
              Book Free Consultation
            </Link>
            <a href="tel:781-237-3166" className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold hover:bg-white/10 transition-colors whitespace-nowrap">
              Call 781-237-3166
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CustomShirtsPage;
